import styled, { keyframes } from "styled-components";

/* ---------- Design tokens ----------
   navy  #0b2a4a   blue #1565c0   accent #ef5a24
   ink   #1a2433   muted #5b6b7f  line #e2e8f0
   wash  #f4f8fc   green #1e8e3e
------------------------------------- */

export const Page = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

/* ============ HERO ============ */
export const Hero = styled.section`
  position: relative;
  width: 100%;
  background:
    linear-gradient(180deg, rgba(8, 26, 46, 0.86) 0%, rgba(8, 26, 46, 0.78) 100%),
    url("https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1600&q=70")
      center / cover no-repeat;
  color: #fff;
`;

export const HeroInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3.5rem 1.5rem 4rem;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 2.5rem 1.25rem 3rem;
  }
`;

export const HeroCopy = styled.div`
  max-width: 600px;

  @media (max-width: 980px) {
    text-align: center;
    margin: 0 auto;
  }
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(239, 90, 36, 0.18);
  border: 1px solid rgba(239, 90, 36, 0.5);
  color: #ffd2bf;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  margin-bottom: 1.1rem;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(2rem, 4.6vw, 3.1rem);
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 1rem;

  span { color: #ffb84d; }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1.02rem, 2vw, 1.18rem);
  color: rgba(255, 255, 255, 0.88);
  margin-bottom: 1.5rem;
`;

export const HeroBullets = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem 1.25rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    font-weight: 500;
    font-size: 0.98rem;
    color: #eaf2fb;
  }
  svg { color: #5bd17a; flex-shrink: 0; }

  @media (max-width: 980px) {
    text-align: left;
    max-width: 380px;
    margin-inline: auto;
    margin-bottom: 1.75rem;
  }
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroCtas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  align-items: center;

  @media (max-width: 980px) { justify-content: center; }
`;

export const PrimaryCall = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: #ef5a24;
  color: #fff;
  font-weight: 700;
  font-size: 1.15rem;
  padding: 0.95rem 1.6rem;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(239, 90, 36, 0.35);
  transition: transform 0.15s ease, background 0.2s ease;

  &:hover { background: #d94a18; transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

export const GhostCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.9rem 1.2rem;
  border-radius: 12px;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover { background: rgba(255, 255, 255, 0.12); border-color: #fff; }
`;

export const HeroRating = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.4rem;
  font-size: 0.92rem;
  color: #eaf2fb;

  .stars { color: #ffc24d; letter-spacing: 1px; }
  strong { color: #fff; }

  @media (max-width: 980px) { justify-content: center; }
`;

/* ============ LEAD FORM CARD ============ */
export const FormCard = styled.form`
  background: #fff;
  color: #1a2433;
  border-radius: 18px;
  padding: 1.75rem;
  box-shadow: 0 30px 60px rgba(4, 18, 33, 0.4);
  width: 100%;
  max-width: 460px;
  justify-self: end;

  @media (max-width: 980px) {
    justify-self: center;
    max-width: 520px;
  }
`;

export const FormHeading = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;

  h2 {
    font-size: 1.45rem;
    color: #0b2a4a;
    margin-bottom: 0.3rem;
  }
  p { color: #5b6b7f; font-size: 0.92rem; margin: 0; }
`;

export const OfferTag = styled.div`
  background: #fff4e6;
  border: 1px dashed #ef9a24;
  color: #b5530a;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: center;
  padding: 0.55rem;
  border-radius: 10px;
  margin-bottom: 1.1rem;
`;

export const Field = styled.div`
  margin-bottom: 0.85rem;

  label {
    display: block;
    font-size: 0.82rem;
    font-weight: 600;
    color: #334e68;
    margin-bottom: 0.3rem;
  }

  input,
  select,
  textarea {
    width: 100%;
    font-family: inherit;
    font-size: 0.98rem;
    color: #1a2433;
    padding: 0.7rem 0.85rem;
    border: 1.5px solid #d6e0ea;
    border-radius: 10px;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      outline: none;
      border-color: #1565c0;
      box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15);
    }
  }
  textarea { resize: vertical; min-height: 72px; }
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const SubmitButton = styled.button`
  width: 100%;
  margin-top: 0.4rem;
  background: #ef5a24;
  color: #fff;
  font-family: inherit;
  font-weight: 700;
  font-size: 1.08rem;
  padding: 0.95rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(239, 90, 36, 0.3);
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover:not(:disabled) { background: #d94a18; transform: translateY(-1px); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
`;

export const PrivacyNote = styled.p`
  margin: 0.85rem 0 0;
  color: #7a8aa0;
  font-size: 0.78rem;
  text-align: center;
`;

export const Honeypot = styled.p`
  display: none;
`;

export const ErrorMessage = styled.div`
  background: #fdecea;
  color: #c0392b;
  border: 1px solid #f5c6c2;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  font-size: 0.88rem;
  margin-bottom: 0.9rem;
`;

/* ============ TRUST STRIP ============ */
export const TrustStrip = styled.section`
  background: #0b2a4a;
  color: #fff;
`;
export const TrustInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  text-align: center;

  .stat strong {
    display: block;
    font-family: 'Poppins', sans-serif;
    font-size: 1.7rem;
    color: #ffb84d;
    line-height: 1.1;
  }
  .stat span { font-size: 0.86rem; color: #cdddee; }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }
`;

/* ============ SECTION SHELL ============ */
export const Section = styled.section`
  padding: 4.5rem 1.5rem;
  background: ${({ $wash }) => ($wash ? "#f4f8fc" : "#fff")};

  @media (max-width: 700px) { padding: 3.25rem 1.25rem; }
`;
export const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;
export const SectionHead = styled.div`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 2.75rem;

  .kicker {
    color: #1565c0;
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  h2 {
    font-size: clamp(1.7rem, 3.4vw, 2.4rem);
    color: #0b2a4a;
    margin: 0.5rem 0 0.6rem;
  }
  p { color: #5b6b7f; font-size: 1.05rem; margin: 0; }
`;

/* ============ SERVICES ============ */
export const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 880px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
export const ServiceCard = styled.div`
  background: #fff;
  border: 1px solid #e6edf5;
  border-radius: 14px;
  padding: 1.6rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  .icon {
    width: 52px;
    height: 52px;
    display: grid;
    place-items: center;
    border-radius: 12px;
    background: linear-gradient(135deg, #1565c0, #0b3d91);
    color: #fff;
    margin-bottom: 1rem;
  }
  h3 { font-size: 1.15rem; color: #0b2a4a; margin-bottom: 0.4rem; }
  p { color: #5b6b7f; font-size: 0.95rem; margin: 0; }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 32px rgba(11, 42, 74, 0.12);
    border-color: #cfe0f3;
  }
`;

/* ============ SERVICE AREAS ============ */
export const AreaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;
export const AreaCard = styled.div`
  background: #fff;
  border: 1px solid #e6edf5;
  border-radius: 14px;
  padding: 1.6rem;
  box-shadow: 0 8px 20px rgba(11, 42, 74, 0.05);

  .county {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.2rem;
    font-weight: 700;
    color: #0b2a4a;
    margin-bottom: 0.75rem;
  }
  .county svg { color: #ef5a24; }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  li {
    font-size: 0.85rem;
    color: #334e68;
    background: #eef4fb;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
  }
`;

/* ============ WHY US ============ */
export const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem 2.5rem;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
export const WhyItem = styled.div`
  display: flex;
  gap: 1rem;

  .badge {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: #e9f6ee;
    color: #1e8e3e;
  }
  h3 { font-size: 1.1rem; color: #0b2a4a; margin-bottom: 0.25rem; }
  p { color: #5b6b7f; font-size: 0.95rem; margin: 0; }
`;

/* ============ REVIEWS ============ */
export const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 880px) { grid-template-columns: 1fr; }
`;
export const ReviewCard = styled.figure`
  background: #fff;
  border: 1px solid #e6edf5;
  border-radius: 14px;
  padding: 1.5rem;
  margin: 0;
  box-shadow: 0 8px 20px rgba(11, 42, 74, 0.05);

  .stars { color: #ffb400; letter-spacing: 1px; margin-bottom: 0.6rem; }
  blockquote { margin: 0 0 1rem; color: #2d3e50; font-size: 0.98rem; line-height: 1.6; }
  figcaption { font-weight: 700; color: #0b2a4a; font-size: 0.92rem; }
  figcaption span { display: block; font-weight: 500; color: #7a8aa0; font-size: 0.82rem; }
`;

/* ============ FINAL CTA ============ */
export const CtaBand = styled.section`
  background: linear-gradient(135deg, #0b2a4a, #134a86);
  color: #fff;
  text-align: center;
  padding: 3.75rem 1.5rem;

  h2 { font-size: clamp(1.7rem, 3.5vw, 2.4rem); margin-bottom: 0.6rem; }
  p { color: #cdddee; font-size: 1.1rem; margin: 0 auto 1.75rem; max-width: 600px; }

  .cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
  }
`;

/* ============ FOOTER ============ */
export const Footer = styled.footer`
  background: #061a2e;
  color: #b8c8d8;
  padding: 3rem 1.5rem 6rem;

  .grid {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 2rem;

    @media (max-width: 760px) { grid-template-columns: 1fr; gap: 1.75rem; }
  }
  h4 { color: #fff; font-size: 1rem; margin-bottom: 0.9rem; }
  p, li, a { font-size: 0.9rem; line-height: 1.7; color: #b8c8d8; }
  ul { list-style: none; padding: 0; margin: 0; }
  a:hover { color: #ffb84d; }
  .brand { font-family: 'Poppins', sans-serif; font-weight: 700; color: #fff; font-size: 1.2rem; margin-bottom: 0.5rem; }
  .foot-call { color: #ffb84d; font-weight: 700; font-size: 1.15rem; }
  .legal {
    max-width: 1200px;
    margin: 2rem auto 0;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 0.82rem;
    color: #7e93a8;
    text-align: center;
  }
`;

/* ============ STICKY MOBILE BAR ============ */
export const MobileCallBar = styled.div`
  display: none;

  @media (max-width: 760px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 200;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.18);

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.45rem;
      padding: 0.95rem;
      font-weight: 700;
      font-size: 1rem;
      color: #fff;
    }
    .call { background: #ef5a24; }
    .quote { background: #1565c0; }
  }
`;

/* ============ SUCCESS ============ */
const pop = keyframes`
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;
export const SuccessCard = styled.div`
  background: #fff;
  color: #1a2433;
  border-radius: 18px;
  padding: 2.25rem 1.75rem;
  text-align: center;
  width: 100%;
  max-width: 460px;
  justify-self: end;
  box-shadow: 0 30px 60px rgba(4, 18, 33, 0.4);
  animation: ${pop} 0.25s ease;

  .check {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #e9f6ee;
    color: #1e8e3e;
    display: grid;
    place-items: center;
    margin: 0 auto 1rem;
  }
  h2 { color: #0b2a4a; margin-bottom: 0.5rem; }
  p { color: #5b6b7f; margin: 0 0 0.5rem; }
  .urgent {
    margin-top: 1.25rem;
    font-weight: 600;
    color: #0b2a4a;
  }
  .urgent a { color: #ef5a24; }

  @media (max-width: 980px) { justify-self: center; max-width: 520px; }
`;
